import { useState, useMemo, useRef, useEffect, Dispatch, SetStateAction } from 'react';

import { NoharaRepresentative } from '../../types/company.types';
import { DepartmentContact } from '../../types/table/company.types';
import { useDepartments } from '../use-departments';
import { useStaffs } from '../use-staffs';
import { findStaffIdByCode, getStaffsFromDepartment } from '../../utils/common-staff-utils';

import { useMarkInitialDetailFieldsTouched } from './use-mark-initial-detail-fields-touched';

import type { ValidationResult } from './types';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { FunctionName, PermissionAction } from '@/lib/enums';
import { getUniqueDepartmentContactLists } from '@/utils/helpers';

// Base interface that all company detail forms should extend
export interface BaseCompanyDetailForm {
  representatives: NoharaRepresentative[];
}

// Department interface for mapper functions
export interface DepartmentForMapper {
  id: string;
  code: number;
  name: string;
  nameKana: string | null;
}

// Config interface for the base hook
export interface CompanyDetailFormConfig<
  TFormData extends BaseCompanyDetailForm,
  TApiResponse,
  TApiRequest,
> {
  // ID from params
  companyId?: string;

  // Initial form data
  initialFormData: TFormData;

  // Validation function
  validateForm: (formData: TFormData) => ValidationResult;

  // API functions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchCompanyById: (id: string) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateCompanyById: (id: string, data: TApiRequest) => Promise<any>;

  // Mapper functions
  mapApiResponseToFormData: (
    apiData: TApiResponse,
    findDepartmentIdByCode: (code: number) => string,
    findStaffIdByCode: (code: number) => string
  ) => TFormData;

  mapFormDataToApiRequest: (formData: TFormData, departments: DepartmentForMapper[]) => TApiRequest;

  // Helper to extract department contact lists from API response
  getDepartmentContactLists: (apiData: TApiResponse) => DepartmentContact[] | undefined;
}

export const useCompanyDetailFormBase = <
  TFormData extends BaseCompanyDetailForm,
  TApiResponse,
  TApiRequest,
>(
  config: CompanyDetailFormConfig<TFormData, TApiResponse, TApiRequest>
) => {
  const {
    companyId,
    initialFormData,
    validateForm,
    fetchCompanyById,
    updateCompanyById,
    mapApiResponseToFormData,
    mapFormDataToApiRequest,
    getDepartmentContactLists,
  } = config;

  // Form state
  const [formData, setFormData] = useState<TFormData>(initialFormData);
  const [originalFormData, setOriginalFormData] = useState<TFormData>(initialFormData);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const { setConfirmDialog } = useConfirmDialog();
  // Loading and error states
  const { isLoading, setIsLoading } = useLoadingProgress();
  const [error, setError] = useState<string | null>(null);

  // Refs
  const hasFetchedData = useRef(false);

  // Global state hooks
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.UPDATE);
  const { staffsByDepartment, loading: staffsLoading, loadStaffsByDepartment } = useStaffs();

  // Helper to normalize representatives by removing metadata fields (canUpdate, originalIndex)
  const normalizeRepresentatives = (reps: NoharaRepresentative[]): NoharaRepresentative[] => {
    return reps.map((rep) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { canUpdate, originalIndex, ...normalized } = rep;
      return normalized as NoharaRepresentative;
    });
  };

  // Helper to normalize form data for comparison (remove metadata fields)
  const normalizeFormDataForComparison = (data: TFormData): TFormData => {
    const normalized = {
      ...data,
      representatives: normalizeRepresentatives(data.representatives),
    };

    return normalized as TFormData;
  };

  // Validation
  const validation = useMemo(() => {
    const result = validateForm(formData);
    return result;
  }, [formData, validateForm, touchedFields]);

  // Check if form has changes compared to original data
  // Normalize data to exclude metadata fields (canUpdate, originalIndex) before comparison
  const hasFormChanges = useMemo(() => {
    const normalizedFormData = normalizeFormDataForComparison(formData);
    const normalizedOriginalData = normalizeFormDataForComparison(originalFormData);
    const formDataStr = JSON.stringify(normalizedFormData);
    const originalDataStr = JSON.stringify(normalizedOriginalData);
    const hasChanges = formDataStr !== originalDataStr;

    return hasChanges;
  }, [formData, originalFormData]);

  // Form field update function
  const updateField = (field: keyof TFormData, value: unknown) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    setTouchedFields((prev) => new Set(prev).add(String(field)));
  };

  // Representative management
  const addRepresentative = () => {
    if (formData.representatives.length < 10) {
      const newRepresentative: NoharaRepresentative = {
        department: '',
        salesPerson: {
          id: '',
          firstName: '',
          lastName: '',
          code: '',
        },
      };
      const newIndex = formData.representatives.length;

      setFormData((prev) => {
        const updated = {
          ...prev,
          representatives: [...prev.representatives, newRepresentative],
        };
        return updated;
      });

      // Mark new representative fields as touched to trigger validation immediately
      setTouchedFields((prev) => {
        const newTouched = new Set(prev);
        newTouched.add(`representatives.${newIndex}.department`);
        newTouched.add(`representatives.${newIndex}.salesPerson`);
        return newTouched;
      });
    }
  };

  const removeRepresentative = (id: string) => {
    const repIndex = Number.parseInt(id);
    if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= formData.representatives.length)
      return;

    if (formData.representatives.length > 1) {
      setFormData((prev) => {
        const updated = {
          ...prev,
          representatives: prev.representatives.filter((_, i) => i !== repIndex),
        };
        return updated;
      });
    }
  };

  const updateRepresentative = (
    id: string,
    field: keyof NoharaRepresentative,
    value: string | { id: string; firstName: string; lastName: string; code: string }
  ) => {
    const repIndex = Number.parseInt(id);
    if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= formData.representatives.length)
      return;

    setFormData((prev) => {
      const updated = {
        ...prev,
        representatives: prev.representatives.map((rep, i) => {
          if (i === repIndex) {
            // If changing department, clear salesPerson
            if (field === 'department') {
              return {
                ...rep,
                [field]: value as string,
                salesPerson: { id: '', firstName: '', lastName: '', code: '' },
              };
            }
            // Handle salesPerson update (can be object)
            if (field === 'salesPerson' && typeof value === 'object') {
              return { ...rep, [field]: value };
            }
            // Handle other string fields
            return { ...rep, [field]: value as string };
          }
          return rep;
        }),
      };
      return updated;
    });

    // Mark the changed field as touched
    setTouchedFields((prev) => {
      const newTouched = new Set(prev);
      newTouched.add(`representatives.${repIndex}.${String(field)}`);

      // If changing department, also mark salesPerson as touched to trigger validation
      if (field === 'department') {
        newTouched.add(`representatives.${repIndex}.salesPerson`);
      }

      return newTouched;
    });
  };

  // Utility functions
  const getFieldError = (fieldName: string): string | undefined => {
    const isTouched = touchedFields.has(fieldName);
    const error = validation.errors.find((e) => e.field === fieldName);
    const errorMessage = error?.message;

    if (!isTouched) return undefined;
    return errorMessage;
  };

  const isFieldValid = (fieldName: string): boolean => {
    if (!touchedFields.has(fieldName)) return true;
    return !validation.errors.some((e) => e.field === fieldName);
  };

  const getSalesPersonOptions = (departmentId: string) => {
    return getStaffsFromDepartment(staffsByDepartment, departmentId);
  };

  // Function to load staffs when department changes
  const handleDepartmentChange = async (departmentId: string) => {
    if (departmentId) {
      try {
        await loadStaffsByDepartment('sale', departmentId, FunctionName.COMPANY_MANAGEMENT);
      } catch (error) {
        console.error('Error loading staffs:', error);
      }
    }
  };

  // Helper functions to convert codes to IDs
  const findDepartmentIdByCode = (code: number): string => {
    const department = departments.find((dept) => dept.code === code);
    return department?.id || '';
  };

  // Helper function to preload staffs for departments
  const preloadStaffsForDepartments = async (departmentContactLists: DepartmentContact[]) => {
    if (!departmentContactLists || departmentContactLists.length === 0) {
      return;
    }
    const uniqueDepartments = getUniqueDepartmentContactLists(
      departmentContactLists as DepartmentContact[]
    );
    const loadStaffsPromises = uniqueDepartments.map(async (department) => {
      if (!department.departmentId || !department.canUpdate) return;

      try {
        await loadStaffsByDepartment(
          'sale',
          department.departmentId,
          FunctionName.COMPANY_MANAGEMENT
        );
      } catch (error) {
        console.error(`Error preloading staffs for department ${department.departmentId}:`, error);
      }
    });

    await Promise.all(loadStaffsPromises);
    // Add small delay to ensure staffs are fully loaded
    await new Promise((resolve) => setTimeout(resolve, 100));
  };

  // Load data from API response into form
  const loadDataFromApi = (apiData: TApiResponse) => {
    const mappedData = mapApiResponseToFormData(apiData, findDepartmentIdByCode, findStaffIdByCode);
    setFormData(mappedData);
    setOriginalFormData(mappedData); // Set original data for comparison
  };

  // API call to fetch company detail
  const fetchCompanyData = async () => {
    // Prevent duplicate fetches
    if (hasFetchedData.current) {
      return;
    }

    if (!companyId) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      hasFetchedData.current = true; // Mark as fetched

      const response = await fetchCompanyById(companyId);

      // API response structure: { data: { data: {...}, timestamp: ..., requestId: ... } }
      const apiData = response.data.data as TApiResponse;

      if (!apiData) {
        throw new Error('No data received from API');
      }

      // Preload staffs for each department in departmentContactLists
      const departmentContactLists = getDepartmentContactLists(apiData);
      if (departmentContactLists) {
        await preloadStaffsForDepartments(departmentContactLists);
      }

      // Load data into form after staffs are loaded
      loadDataFromApi(apiData);
    } catch (err) {
      setError('データの取得に失敗しました。');
      console.error('Error fetching company:', err);
      hasFetchedData.current = false; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch data when component mounts
  useEffect(() => {
    setIsLoading(true);
    if (companyId && !hasFetchedData.current) {
      void fetchCompanyData();
    }
  }, [companyId]);

  // Mark all representative fields as touched when data is loaded
  useMarkInitialDetailFieldsTouched({
    formData,
    touchedFields,
    setTouchedFields,
    hasFetchedData,
    validateForm,
  });

  // Handle form submission (edit)
  const handleSubmit = async (): Promise<boolean> => {
    if (!companyId) {
      return false;
    }

    if (!validation.isValid) {
      // Mark all fields as touched to show all errors on submit attempt
      const allFieldNames = new Set(
        Object.keys(formData).filter((key) => typeof formData[key as keyof TFormData] !== 'object')
      );
      for (const [index] of formData.representatives.entries()) {
        allFieldNames.add(`representatives.${index}.department`);
        allFieldNames.add(`representatives.${index}.salesPerson`);
      }
      setTouchedFields(allFieldNames);
      return false;
    }

    setIsLoading(true);

    try {
      // Map form data to API request format
      const apiRequest = mapFormDataToApiRequest(formData, departments);
      const response = await updateCompanyById(companyId, apiRequest);

      if (response.status === 200 || response.status === 201) {
        return true;
      } else {
        console.error('Error updating company:', response.statusText);
        setError('更新に失敗しました。');
        return false;
      }
    } catch (error) {
      console.error('Error updating company:', error);
      setConfirmDialog({
        title: '更新に失敗しました。もう一度お試しください。',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form to original state
  const resetForm = () => {
    setFormData(originalFormData);
    setTouchedFields(new Set());
    setError(null);
  };

  const canAddRepresentative = formData.representatives.length < 10;
  const isSubmitDisabled = !validation.isValid || !hasFormChanges || isLoading;

  return {
    // Data
    companyId,
    formData,
    originalFormData,
    setFormData: setFormData as Dispatch<SetStateAction<TFormData>>,

    // State
    touchedFields,
    isLoading,
    error,

    // Validation
    validation: {
      errors: validation.errors,
      isValid: validation.isValid,
    },
    hasFormChanges,

    // Form actions
    updateField,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    resetForm,

    // Utility functions
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    handleDepartmentChange,

    // Computed properties
    canAddRepresentative,
    isSubmitDisabled,

    // External data
    departments,
    departmentsLoading,
    staffsLoading,
    staffsByDepartment,
  };
};
